import React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@/lib/utils/validation/zod-react-hook-form-resolver";
import { Client } from "@/lib/types/database";
import { useFormFeedback } from "@/lib/hooks/forms/use-feedback";
import { FormFeedbackDisplay } from "@/components/ui/states/feedback-display";
import { cleanFormData, clientToEditFormDefaults } from "@/lib/utils/crm/form-helpers";
import { ResponsiveModal , scrollToFirstError } from "@west-tennessee-consulting/ui";
import { ClientEditFormSections } from "./forms/ClientEditFormSections";
import { EditClientFormSchema, type EditClientForm } from "@/lib/schemas/contact";

interface ClientEditModalProps {
  client: Client;
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: Partial<EditClientForm>) => Promise<void>;
  onViewFullDetails?: () => void;
}

export const ClientEditModal: React.FC<ClientEditModalProps> = ({
  client,
  isOpen,
  onClose,
  onSave,
  onViewFullDetails,
}) => {
  const feedback = useFormFeedback({
    successMessage: "Contact updated successfully!",
  });

  const form = useForm<EditClientForm>({
    resolver: zodResolver(EditClientFormSchema as any),
    defaultValues: clientToEditFormDefaults(client),
    mode: "onBlur",
  });

  const handleSubmit = async (data: EditClientForm) => {
    try {
      feedback.dismissError();
      const cleanedData = cleanFormData(data);
      await onSave(cleanedData);
      feedback.showSuccess();
      onClose();
    } catch (error: any) {
      feedback.showError(error);
    }
  };

  return (
    <ResponsiveModal
      open={isOpen}
      onOpenChange={(open) => !open && onClose()}
      title={`Edit Contact: ${client.name}`}
      maxWidth="4xl"
      side="bottom"
    >
      <FormFeedbackDisplay feedback={feedback} />
      <form onSubmit={form.handleSubmit(handleSubmit, scrollToFirstError)} className="space-y-4">
        <ClientEditFormSections editForm={form} />
        <div className="flex gap-2 justify-end pt-4">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 dark:text-gray-300 dark:bg-gray-900 dark:border-gray-700 dark:hover:bg-gray-800"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={form.formState.isSubmitting}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {form.formState.isSubmitting ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </form>
    </ResponsiveModal>
  );
};
